import React, { useState } from "react"
import type { StyleProps } from "@mallardbay/lib-react-components"
import {
    Button,
    HorizontalStack,
    useColors,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTag } from "@fortawesome/free-solid-svg-icons/faTag"

import type { QuoteFieldsFragment } from "~graphql/generated/graphql"
import {
    useRemoveQuoteCouponMutation,
    useApplyQuoteCouponMutation,
} from "~graphql/generated/graphql"
import { formatPrice } from "~utils/helpers"
import { useBookTrip } from "~components/pages/book/book-trip-provider"
import COPY from "~config/copy-constants"
import { trackSelectPromotion } from "~utils/event-tracking/event-tracking-helpers"
import {
    Text,
    TextBold,
    TextError,
    TextLarge,
} from "~components/shared/todo-lib-react-components/text"
import { TextButtonSmall } from "~components/shared/todo-lib-react-components/text-button"
import { VerticalStack } from "~components/shared/todo-lib-react-components/vertical-stack"
import Input from "~components/shared/todo-lib-react-components/input"

interface Props {
    readonly useBookTripHook?: () => {
        quote: QuoteFieldsFragment | null
        refetchQuote: () => void
        isLoadingQuote: boolean
    }
}

export default function BookingCoupon({
    useBookTripHook = useBookTrip,
}: Props) {
    const styles = useStyles()
    const [couponCode, setCouponCode] = useState("")
    const [couponError, setCouponError] = useState("")

    const { quote, refetchQuote, isLoadingQuote } = useBookTripHook()

    const [apply, { loading: isApplyLoading }] = useApplyQuoteCouponMutation({
        variables: { quoteId: quote?.id ?? "", couponCode },
        onCompleted: () => {
            refetchQuote()
            setCouponCode("")
            trackSelectPromotion({ promotion_id: couponCode })
        },
        onError: (error) => setCouponError(error.message),
    })

    const [remove, { loading: isRemoveLoading }] = useRemoveQuoteCouponMutation(
        {
            variables: { quoteId: quote?.id ?? "" },
            onCompleted: refetchQuote,
            onError: (error) => setCouponError(error.message),
        }
    )

    const applyCoupon = () => {
        setCouponError("")
        void apply()
    }

    const removeCoupon = () => {
        setCouponError("")
        void remove()
    }

    const coupon = quote?.coupon
    const isLoading = isApplyLoading || isRemoveLoading || isLoadingQuote

    return (
        <VerticalStack style={styles.root}>
            <TextLarge>{COPY.APPLY_A_COUPON}</TextLarge>
            <HorizontalStack>
                <Input
                    value={couponCode}
                    onChange={(newCode) =>
                        setCouponCode(newCode.trim().toUpperCase())
                    }
                    startIcon={<FontAwesomeIcon icon={faTag} />}
                    hasError={Boolean(couponError)}
                    placeholder={COPY.ENTER_COUPON_CODE}
                />
                <Button
                    onClick={applyCoupon}
                    isLoading={isLoading}
                    disabled={!couponCode}
                    variant="solid"
                >
                    {COPY.APPLY}
                </Button>
            </HorizontalStack>
            {couponError && <TextError>{couponError}</TextError>}
            {coupon && (
                <HorizontalStack>
                    <HorizontalStack style={styles.couponContainer}>
                        <TextBold style={styles.couponCode}>
                            {coupon.custom_code}
                        </TextBold>
                        <Text>{formatCouponValue(coupon)}</Text>
                    </HorizontalStack>
                    <TextButtonSmall
                        onClick={removeCoupon}
                        isLoading={isLoading}
                        isUnderlined
                    >
                        {COPY.REMOVE}
                    </TextButtonSmall>
                </HorizontalStack>
            )}
        </VerticalStack>
    )
}

function useStyles() {
    const colors = useColors()

    const root: StyleProps = {
        gap: 3,
        alignItems: "stretch",
    }
    const couponContainer: StyleProps = {
        gap: 1,
    }
    const couponCode: StyleProps = {
        color: colors.successColor,
    }

    return { root, couponContainer, couponCode }
}

function formatCouponValue(coupon: NonNullable<QuoteFieldsFragment["coupon"]>) {
    const amountOff = coupon.amount_off ? formatPrice(coupon.amount_off) : null
    const percentOff = coupon.percent_off ? `${coupon.percent_off}%` : null

    if (!amountOff && !percentOff) return null

    return ` - ${amountOff ?? percentOff} ${COPY.OFF}`
}
